import type { SanityImageSource } from '@sanity/image-url';
import { createImageUrlBuilder } from '@sanity/image-url';
import { dataset, projectId } from '@/lib/sanity/core/config';
import type { MediaDto } from '@/lib/sanity/dto';

// Plain project coordinates, not the `client` instance: Client Components import
// the helpers below, and passing the client would ship `createClient` with them.
const builder = createImageUrlBuilder({ projectId, dataset });

export function urlForImage(source: SanityImageSource) {
    return builder.image(source);
}

/** Widest variant a phone ever needs for a full-bleed section background. */
const SECTION_MOBILE_WIDTH = 768;

/**
 * Quality of URLs the browser downloads as they are — `og` for crawlers, `gallery`
 * in the lightbox, `heroPoster` as a `<video poster>`, the section backgrounds in
 * CSS. For those the CDN copy is the final file.
 */
const IMAGE_QUALITY = 80;

/**
 * Quality of the CDN source next/image re-encodes. Those URLs are an intermediate
 * the optimizer fetches server-side once, never a file the browser downloads, so
 * they stay near-lossless: compressing at 80 here and again in the optimizer costs
 * quality twice for the same delivered bytes.
 */
const OPTIMIZER_SOURCE_QUALITY = 90;

export function sanityImageSection(img?: MediaDto, maxWidth = 1920) {
    return img?.asset
        ? builder.image(img).width(maxWidth).quality(IMAGE_QUALITY).auto('format').fit('max').url()
        : undefined;
}

/**
 * Custom properties for a section background: a phone-sized variant plus the
 * full-width one, swapped by the `SECTION_BACKGROUND` classes. A CSS
 * `background-image` cannot carry a srcset, so without this a phone downloads
 * the desktop-sized file.
 */
export function sanityImageSectionStyle(img?: MediaDto, maxWidth = 1920): Record<string, string> {
    const mobile = sanityImageSection(img, Math.min(SECTION_MOBILE_WIDTH, maxWidth));
    const desktop = sanityImageSection(img, maxWidth);

    if (!mobile || !desktop) return {};

    return {
        '--bg-image': `url(${mobile})`,
        '--bg-image-lg': `url(${desktop})`,
    };
}

export const sanityImage = {
    og: (img?: MediaDto) =>
        img?.asset ? builder.image(img).width(1200).height(630).fit('max').quality(IMAGE_QUALITY).url() : undefined,

    card: (img?: MediaDto) =>
        img?.asset
            ? builder.image(img).width(1000).quality(OPTIMIZER_SOURCE_QUALITY).auto('format').fit('max').url()
            : undefined,

    // A card that renders half the viewport wide asks the optimizer for up to
    // ~2000px on a retina screen, which the 1000px `card` source can only upscale.
    cardLarge: (img?: MediaDto) =>
        img?.asset
            ? builder.image(img).width(2000).quality(OPTIMIZER_SOURCE_QUALITY).auto('format').fit('max').url()
            : undefined,

    heroImage: (img?: MediaDto) =>
        img?.asset ? builder.image(img).width(120).quality(OPTIMIZER_SOURCE_QUALITY).url() : undefined,

    heroBackground: (img?: MediaDto) =>
        img?.asset
            ? builder.image(img).width(2200).quality(OPTIMIZER_SOURCE_QUALITY).auto('format').fit('max').url()
            : undefined,

    // <video poster> takes a single URL and cannot be responsive, so keep it lean.
    heroPoster: (img?: MediaDto) =>
        img?.asset ? builder.image(img).width(1600).quality(IMAGE_QUALITY).auto('format').fit('max').url() : undefined,

    // Publisher logo in a logo wall: a few hundred px wide at most, and the
    // optimizer re-encodes it, so the source stays near-lossless.
    logo: (img?: MediaDto) =>
        img?.asset
            ? builder.image(img).width(600).quality(OPTIMIZER_SOURCE_QUALITY).auto('format').fit('max').url()
            : undefined,

    gallery: (img?: MediaDto) =>
        img?.asset ? builder.image(img).width(2200).quality(IMAGE_QUALITY).auto('format').fit('max').url() : undefined,
};

export type SanityImageVariant = keyof typeof sanityImage;
